import { useEffect, useMemo, useState } from 'react';
import {
  workOrderTimeline as api,
  type StageStatus, type WorkOrderEventInput, type WorkOrderStageView, type WorkOrderTimeline,
  type WorkOrderTimelineMeta,
} from '@/api/endpoints';
import { Icon } from './Icon';

const STAGE_CLS: Record<StageStatus, string> = {
  DONE: 'bg-green-50 border-green-300 text-green-800',
  IN_PROGRESS: 'bg-amber-50 border-amber-400 text-amber-800',
  TODO: 'bg-white border-ink-200 text-ink-500',
  SKIPPED: 'bg-white border-dashed border-ink-200 text-ink-300 line-through',
};
const STAGE_DOT: Record<StageStatus, string> = {
  DONE: '✓', IN_PROGRESS: '●', TODO: '○', SKIPPED: '–',
};
const WARN_CLS = {
  danger: 'text-red-700 bg-red-50 border-red-200',
  warn: 'text-amber-800 bg-amber-50 border-amber-200',
  info: 'text-water-700 bg-water-50 border-water-200',
};

const fmt = (d: string | null) => (d ? d.split('-').reverse().join('.') : '');
const minusDays = (iso: string, days: number) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const trackingUrl = (nr: string) =>
  /^\d{24}$/.test(nr.replace(/\s/g, ''))
    ? 'https://inpost.pl/sledzenie-przesylek?number=' + nr.replace(/\s/g, '')
    : 'https://emonitoring.poczta-polska.pl/?numer=' + encodeURIComponent(nr.trim());

/**
 * Oś czasu zlecenia na roboty: fazy od oferty do „na przyszłość”, wpisy ręczne i wyliczone
 * (zlecenie, dni w terenie, faktury, zapłaty). {@code workId} = widok z karty jednej pracy —
 * pokazuje wpisy tej studni i wpisy ogólne zlecenia.
 */
export function WorkOrderTimelineCard({ orderId, canEdit, workId }: {
  orderId: number;
  canEdit: boolean;
  workId?: number;
}) {
  const [meta, setMeta] = useState<WorkOrderTimelineMeta | null>(null);
  const [t, setT] = useState<WorkOrderTimeline | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [stageEdit, setStageEdit] = useState<WorkOrderStageView | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => { api.meta().then(setMeta).catch(() => {}); }, []);
  useEffect(() => {
    setT(null);
    api.get(orderId).then(setT).catch(e => setErr(e?.message ?? String(e)));
  }, [orderId]);

  const events = useMemo(() => (t?.events ?? [])
    .filter(e => workId == null || e.workId == null || e.workId === workId)
    .slice().reverse(), [t, workId]);

  const run = (p: Promise<WorkOrderTimeline>) => {
    setErr(null);
    return p.then(x => { setT(x); return x; }).catch(e => { setErr(e?.message ?? String(e)); throw e; });
  };

  if (!t) {
    return (
      <div className="card">
        <div className="card-head"><div className="card-title">Oś czasu zlecenia</div></div>
        <div className="card-body text-[13px] text-ink-400">{err ? <span className="text-red-600">{err}</span> : 'Ładowanie…'}</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Oś czasu zlecenia</div>
        {t.deadline && <span className="ml-auto text-[12px] text-ink-500">Termin umowny: <b className="text-ink-800">{fmt(t.deadline)}</b></span>}
        {canEdit && (
          <button onClick={() => setAdding(a => !a)} className={(t.deadline ? '' : 'ml-auto ') + 'btn btn-secondary btn-sm'}>
            <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj wpis
          </button>
        )}
      </div>
      <div className="card-body">
        {/* Fazy */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {t.stages.map(s => (
            <button key={s.code} type="button" disabled={!canEdit}
                    onClick={() => setStageEdit(stageEdit?.code === s.code ? null : s)}
                    title={(s.auto ? 'Stan wyliczony z danych' : 'Ustawione ręcznie' + (s.updatedByName ? ' — ' + s.updatedByName : ''))
                      + (s.note ? '\n' + s.note : '')}
                    className={'border rounded px-2 py-1 text-[12px] leading-tight text-left min-w-[92px] '
                      + STAGE_CLS[s.status]
                      + (s.code === t.currentStage ? ' ring-2 ring-water-400' : '')
                      + (canEdit ? ' hover:shadow-sm cursor-pointer' : ' cursor-default')}>
              <div className="font-semibold">{STAGE_DOT[s.status]} {s.label}{!s.auto && <span className="text-ink-400"> ✎</span>}</div>
              <div className="text-[11px] opacity-80">{s.date ? fmt(s.date) : s.statusName}</div>
            </button>
          ))}
        </div>

        {stageEdit && canEdit && (
          <StageForm key={stageEdit.code} stage={stageEdit} statuses={meta?.statuses ?? []}
                     onCancel={() => setStageEdit(null)}
                     onSave={d => run(api.setStage(orderId, stageEdit.code, d)).then(() => setStageEdit(null))}
                     onReset={() => run(api.clearStage(orderId, stageEdit.code)).then(() => setStageEdit(null))}/>
        )}

        {/* Studnie */}
        {workId == null && t.works.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3 text-[12px]">
            {t.works.map(w => (
              <span key={w.id} className={'pill pill-' + w.statusColor}>{w.label}: {w.statusName}</span>
            ))}
          </div>
        )}

        {t.warnings.length > 0 && (
          <div className="flex flex-col gap-1 mb-3">
            {t.warnings.map((w, i) => (
              <div key={i} className={'border rounded px-2.5 py-1.5 text-[12px] ' + WARN_CLS[w.level]}>{w.text}</div>
            ))}
          </div>
        )}

        {adding && canEdit && meta && (
          <EventForm meta={meta} works={t.works} defaultStage={t.currentStage} defaultWorkId={workId ?? null}
                     onCancel={() => setAdding(false)}
                     onSave={d => run(api.addEvent(orderId, d)).then(() => setAdding(false))}/>
        )}

        {err && <div className="text-[12px] text-red-600 mb-2">{err}</div>}

        {/* Wpisy */}
        {events.length === 0 && <div className="text-[13px] text-ink-400">Brak wpisów.</div>}
        <ul className="m-0 p-0 list-none">
          {events.map((e, i) => (
            <li key={(e.id ?? 'a') + '-' + i} className="py-1.5 border-b border-ink-50 last:border-0 text-[13px]">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[12px] text-ink-500 shrink-0 w-[78px]">{fmt(e.date)}</span>
                <span className="pill pill-earth shrink-0">{e.stageName}</span>
                <span className={'min-w-0 ' + (e.auto ? 'text-ink-600' : 'text-ink-800')}>
                  {e.title}
                  {e.workLabel && workId == null && <span className="text-ink-500"> · {e.workLabel}</span>}
                  {e.trackingNumber && (
                    <> · <a href={trackingUrl(e.trackingNumber)} target="_blank" rel="noreferrer"
                            className="font-mono text-[12px] text-water-600 hover:underline">{e.trackingNumber}</a></>
                  )}
                </span>
                <span className="ml-auto text-[11px] text-ink-400 shrink-0">{e.auto ? 'z danych' : e.createdByName}</span>
                {canEdit && !e.auto && e.id != null && (
                  <button className="btn btn-ghost btn-icon shrink-0" title="Usuń wpis"
                          onClick={() => { if (confirm('Usunąć wpis „' + e.title + '”?')) run(api.deleteEvent(orderId, e.id!)); }}>
                    <Icon name="x" className="w-3 h-3"/>
                  </button>
                )}
              </div>
              {e.note && <div className="text-[12px] text-ink-600 whitespace-pre-line ml-[86px]">{e.note}</div>}
              {e.remindOn && (
                <div className="text-[12px] text-water-700 ml-[86px]">
                  Przypomnienie {fmt(e.remindOn)}{e.reminderSent ? ' — wysłane' : ''}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StageForm({ stage, statuses, onCancel, onSave, onReset }: {
  stage: WorkOrderStageView;
  statuses: { code: StageStatus; label: string }[];
  onCancel: () => void;
  onSave: (d: { status: StageStatus; date: string | null; note: string | null }) => Promise<unknown>;
  onReset: () => Promise<unknown>;
}) {
  const [status, setStatus] = useState<StageStatus>(stage.status);
  const [date, setDate] = useState<string>(stage.date ?? '');
  const [note, setNote] = useState<string>(stage.note ?? '');
  const [busy, setBusy] = useState(false);
  const go = (p: () => Promise<unknown>) => { setBusy(true); p().catch(() => {}).finally(() => setBusy(false)); };
  return (
    <div className="border border-ink-200 rounded p-3 mb-3 bg-ink-50 grid gap-2 text-[13px]"
         style={{ gridTemplateColumns: '160px 150px minmax(0,1fr)' }}>
      <div className="col-span-3 font-semibold text-ink-800">
        {stage.label}
        <span className="font-normal text-ink-500 text-[12px]">
          {stage.auto ? ' — teraz wyliczane z danych' : ' — ustawione ręcznie'}
        </span>
      </div>
      <select className="input" value={status} onChange={e => setStatus(e.target.value as StageStatus)}>
        {statuses.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
      </select>
      <input type="date" className="input" value={date} onChange={e => setDate(e.target.value)}/>
      <input className="input" placeholder="Uwaga (opcjonalnie)" value={note} onChange={e => setNote(e.target.value)}/>
      <div className="col-span-3 flex gap-2 justify-end">
        {!stage.auto && (
          <button type="button" disabled={busy} className="btn btn-ghost btn-sm mr-auto" onClick={() => go(onReset)}>
            Wróć do stanu z danych
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Anuluj</button>
        <button type="button" disabled={busy} className="btn btn-primary btn-sm"
                onClick={() => go(() => onSave({ status, date: date || null, note: note.trim() || null }))}>Zapisz</button>
      </div>
    </div>
  );
}

function EventForm({ meta, works, defaultStage, defaultWorkId, onCancel, onSave }: {
  meta: WorkOrderTimelineMeta;
  works: WorkOrderTimeline['works'];
  defaultStage: string;
  defaultWorkId: number | null;
  onCancel: () => void;
  onSave: (d: WorkOrderEventInput) => Promise<unknown>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [d, setD] = useState<WorkOrderEventInput>({
    stage: defaultStage, date: today, title: '', note: null,
    workId: defaultWorkId ?? (works.length === 1 ? works[0].id : null), trackingNumber: null, remindOn: null,
  });
  const [busy, setBusy] = useState(false);
  const hints = meta.stages.find(s => s.code === d.stage)?.hints ?? [];
  const przyszlosc = d.stage === 'NA_PRZYSZLOSC';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    onSave({ ...d, title: d.title.trim(), note: d.note?.trim() || null, trackingNumber: d.trackingNumber?.trim() || null })
      .catch(() => {}).finally(() => setBusy(false));
  };

  return (
    <form onSubmit={submit} className="border border-ink-200 rounded p-3 mb-3 grid gap-2 text-[13px]"
          style={{ gridTemplateColumns: '170px 150px minmax(0,1fr)' }}>
      <select className="input" value={d.stage}
              onChange={e => {
                const stage = e.target.value;
                setD({ ...d, stage, title: '',
                  remindOn: stage === 'NA_PRZYSZLOSC' && d.date ? minusDays(d.date, 60) : null });
              }}>
        {meta.stages.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
      </select>
      <input type="date" className="input" value={d.date ?? ''} title={przyszlosc ? 'Kiedy (termin przyszłej pracy)' : 'Data'}
             onChange={e => setD({ ...d, date: e.target.value || null,
               remindOn: przyszlosc && e.target.value ? minusDays(e.target.value, 60) : d.remindOn })}/>
      <input className="input" list="wo-hints" autoFocus placeholder="Co się wydarzyło — wybierz z listy albo wpisz"
             value={d.title} onChange={e => setD({ ...d, title: e.target.value })}/>
      <datalist id="wo-hints">{hints.map(h => <option key={h} value={h}/>)}</datalist>
      <select className="input" value={d.workId ?? ''}
              onChange={e => setD({ ...d, workId: e.target.value ? Number(e.target.value) : null })}>
        <option value="">— całe zlecenie —</option>
        {works.map(w => <option key={w.id} value={w.id}>{w.label}</option>)}
      </select>
      {d.stage === 'WYSYLKA' ? (
        <input className="input font-mono col-span-2" placeholder="Numer nadania (InPost / Poczta Polska)"
               value={d.trackingNumber ?? ''} onChange={e => setD({ ...d, trackingNumber: e.target.value })}/>
      ) : przyszlosc ? (
        <label className="col-span-2 flex items-center gap-2 text-[12px] text-ink-600">
          Przypomnij
          <input type="date" className="input !w-[150px]" value={d.remindOn ?? ''}
                 onChange={e => setD({ ...d, remindOn: e.target.value || null })}/>
          <span className="text-ink-400">(domyślnie 2 miesiące wcześniej — czas na ofertę)</span>
        </label>
      ) : <div className="col-span-2"/>}
      <textarea className="input col-span-3" rows={2} placeholder="Szczegóły (opcjonalnie)" value={d.note ?? ''}
                onChange={e => setD({ ...d, note: e.target.value })}/>
      <div className="col-span-3 flex gap-2 justify-end">
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Anuluj</button>
        <button type="submit" disabled={busy || !d.title.trim()} className="btn btn-primary btn-sm">Zapisz wpis</button>
      </div>
    </form>
  );
}
